import React, { useMemo, useCallback, useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { formatValue } from './FormatPresets';
import { evaluateCondition } from './ConditionalFormatDialog';
import { evaluateFormula, parseFormula } from '../../utils/formulaParser';

// Register AG Grid community modules
ModuleRegistry.registerModules([AllCommunityModule]);

// Columns that should always be treated as numbers
const NUMERIC_KEYS = new Set([
  'hours_worked', 'total_pay', 'hourly_rate', 'paid_amount', 'break_minutes',
  'subtotal', 'tax_amount', 'total_amount', 'entry_count',
]);

// Columns that should have default currency format
const CURRENCY_KEYS = new Set([
  'total_pay', 'hourly_rate', 'paid_amount', 'subtotal', 'tax_amount', 'total_amount',
]);

// Columns that should have default number format
const NUMBER_KEYS = new Set(['hours_worked', 'break_minutes', 'entry_count']);

// Date column keys
const DATE_KEYS = new Set([
  'entry_date', 'start_date', 'end_date', 'invoice_date', 'due_date', 'paid_at',
]);

function coerceNumeric(val) {
  if (val === null || val === undefined || val === '') return null;
  if (typeof val === 'number') return val;
  // Strip currency symbols and commas before parsing (backend may return "$123.45")
  const cleaned = typeof val === 'string' ? val.replace(/[$,]/g, '').trim() : val;
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
}

// ============================================================
// Custom filter component that always passes subtotal rows
// Supports text (contains), number (min/max), and date (from/to)
// ============================================================
const SubtotalSafeFilter = forwardRef((props, ref) => {
  const colKey = props.colDef.field;
  const isNumeric = NUMERIC_KEYS.has(colKey) || CURRENCY_KEYS.has(colKey);
  const isDate = DATE_KEYS.has(colKey);

  const [filterValue, setFilterValue] = useState('');
  const [filterValueTo, setFilterValueTo] = useState('');

  useImperativeHandle(ref, () => ({
    doesFilterPass(params) {
      // Subtotal rows always pass
      if (params.data?.__isSubtotal) return true;
      if (!filterValue && !filterValueTo) return true;

      const rawValue = params.data?.[colKey];

      if (isDate) {
        if (!rawValue) return false;
        const dateStr = rawValue instanceof Date ? rawValue.toISOString().split('T')[0] : String(rawValue).substring(0, 10);
        if (filterValue && dateStr < filterValue) return false;
        if (filterValueTo && dateStr > filterValueTo) return false;
        return true;
      }

      if (isNumeric) {
        const numVal = coerceNumeric(rawValue);
        if (numVal === null) return false;
        if (filterValue !== '' && numVal < parseFloat(filterValue)) return false;
        if (filterValueTo !== '' && numVal > parseFloat(filterValueTo)) return false;
        return true;
      }

      // Text: contains
      return String(rawValue ?? '').toLowerCase().includes(filterValue.toLowerCase());
    },
    isFilterActive() {
      return !!filterValue || !!filterValueTo;
    },
    getModel() {
      if (!filterValue && !filterValueTo) return null;
      return { filter: filterValue, filterTo: filterValueTo };
    },
    setModel(model) {
      setFilterValue(model?.filter || '');
      setFilterValueTo(model?.filterTo || '');
    },
  }));

  const onValueChange = (val) => {
    setFilterValue(val);
    props.filterChangedCallback();
  };
  const onValueToChange = (val) => {
    setFilterValueTo(val);
    props.filterChangedCallback();
  };

  const inputStyle = {
    width: '100%', padding: '5px 8px', border: '1px solid #babfc7',
    borderRadius: 3, fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: 11, color: '#666', marginBottom: 2, display: 'block' };

  if (isDate) {
    return (
      <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180 }}>
        <label style={labelStyle}>From:</label>
        <input type="date" value={filterValue} onChange={e => onValueChange(e.target.value)} style={inputStyle} />
        <label style={labelStyle}>To:</label>
        <input type="date" value={filterValueTo} onChange={e => onValueToChange(e.target.value)} style={inputStyle} />
        {(filterValue || filterValueTo) && (
          <button
            onClick={() => { setFilterValue(''); setFilterValueTo(''); props.filterChangedCallback(); }}
            style={{ ...inputStyle, cursor: 'pointer', background: '#f5f5f5', textAlign: 'center', marginTop: 4 }}
          >Clear</button>
        )}
      </div>
    );
  }

  if (isNumeric) {
    return (
      <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 160 }}>
        <label style={labelStyle}>Min:</label>
        <input type="number" value={filterValue} onChange={e => onValueChange(e.target.value)} style={inputStyle} placeholder="No minimum" step="any" />
        <label style={labelStyle}>Max:</label>
        <input type="number" value={filterValueTo} onChange={e => onValueToChange(e.target.value)} style={inputStyle} placeholder="No maximum" step="any" />
        {(filterValue || filterValueTo) && (
          <button
            onClick={() => { setFilterValue(''); setFilterValueTo(''); props.filterChangedCallback(); }}
            style={{ ...inputStyle, cursor: 'pointer', background: '#f5f5f5', textAlign: 'center', marginTop: 4 }}
          >Clear</button>
        )}
      </div>
    );
  }

  // Text filter
  return (
    <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180 }}>
      <input
        type="text" value={filterValue} onChange={e => onValueChange(e.target.value)}
        style={inputStyle} placeholder="Contains..."
      />
      {filterValue && (
        <button
          onClick={() => { setFilterValue(''); props.filterChangedCallback(); }}
          style={{ ...inputStyle, cursor: 'pointer', background: '#f5f5f5', textAlign: 'center', marginTop: 4 }}
        >Clear</button>
      )}
    </div>
  );
});

SubtotalSafeFilter.displayName = 'SubtotalSafeFilter';

// ============================================================
// Main Grid Component
// ============================================================
export default function ReportBuilderGrid({
  rowData,
  columns,
  columnSettings,
  columnOrder,
  calculatedColumns,
  conditionalFormatting,
  groupConfig,
  showTotalsRow,
  onColumnMoved,
  onColumnResized,
  onColumnSelected,
  gridRef: externalGridRef,
}) {
  const internalGridRef = useRef(null);
  const gridRef = externalGridRef || internalGridRef;

  // Pre-parse calculated column formulas
  const parsedFormulas = useMemo(() => {
    const parsed = {};
    if (calculatedColumns) {
      Object.entries(calculatedColumns).forEach(([key, calc]) => {
        try {
          parsed[key] = parseFormula(calc.formula);
        } catch (e) {
          console.warn(`Invalid formula for ${key}:`, e.message);
        }
      });
    }
    return parsed;
  }, [calculatedColumns]);

  // Compute calculated columns + coerce numeric values for each row
  const processedData = useMemo(() => {
    if (!rowData) return [];
    return rowData.map(row => {
      const enhanced = { ...row };
      // Coerce known numeric fields to actual numbers
      for (const key of Object.keys(enhanced)) {
        if (NUMERIC_KEYS.has(key)) {
          enhanced[key] = coerceNumeric(enhanced[key]);
        }
      }
      // Compute calculated columns
      if (calculatedColumns) {
        Object.entries(calculatedColumns).forEach(([key, calc]) => {
          const ast = parsedFormulas[key];
          if (ast) {
            try {
              enhanced[key] = evaluateFormula(ast, enhanced);
            } catch (e) {
              enhanced[key] = null;
            }
          }
        });
      }
      return enhanced;
    });
  }, [rowData, calculatedColumns, parsedFormulas]);

  // Track column order in a ref so drag-reorder doesn't trigger re-render
  const columnOrderRef = useRef(columnOrder);
  useEffect(() => {
    columnOrderRef.current = columnOrder;
  }, [columnOrder]);

  // Build column definitions from config
  const columnDefs = useMemo(() => {
    const order = columnOrderRef.current || columns.map(c => c.key);
    const defs = [];

    order.forEach(colKey => {
      const colDef = columns.find(c => c.key === colKey);
      const settings = columnSettings?.[colKey] || {};
      const isCalc = calculatedColumns?.[colKey];
      const label = settings.headerName || isCalc?.headerName || colDef?.label || colKey;
      const format = settings.format || isCalc?.format;

      const def = {
        field: colKey,
        headerName: label,
        sortable: true,
        resizable: true,
        filter: SubtotalSafeFilter,
      };

      // Width
      if (settings.width) def.width = settings.width;
      else def.flex = 1;

      // Pinned (frozen)
      if (settings.pinned) def.pinned = settings.pinned;

      // Word wrap
      if (settings.wrapText) {
        def.wrapText = true;
        def.autoHeight = true;
        def.cellStyle = { ...(def.cellStyle || {}), whiteSpace: 'normal', lineHeight: '1.4' };
      }

      // Determine alignment — user setting > auto-detect for numeric > left
      const isNumeric = NUMERIC_KEYS.has(colKey) || CURRENCY_KEYS.has(colKey) || !!isCalc;
      const alignment = settings.alignment || (isNumeric ? 'right' : 'left');
      if (alignment !== 'left') {
        def.cellStyle = { ...(def.cellStyle || {}), textAlign: alignment };
        def.headerClass = `ag-header-${alignment}`;
      }

      // Determine format — user setting > auto-detect
      const effectiveFormat = format
        || (CURRENCY_KEYS.has(colKey) ? { type: 'currency', symbol: '$', decimals: 2 } : null)
        || (NUMBER_KEYS.has(colKey) ? { type: 'number', decimals: 2 } : null);

      if (effectiveFormat) {
        def.valueFormatter = (params) => formatValue(params.value, effectiveFormat);
      }

      // Grouping — don't set rowGroup/aggFunc; we handle subtotals manually
      defs.push(def);
    });

    return defs;
  }, [columns, columnSettings, calculatedColumns, groupConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  // Insert group subtotal rows into processed data when groupConfig is active
  // Supports both single string groupBy and array for nested grouping
  const displayData = useMemo(() => {
    if (!groupConfig?.groupBy || !processedData?.length) return processedData;

    // Normalize groupBy to array
    const groupByKeys = Array.isArray(groupConfig.groupBy)
      ? groupConfig.groupBy.filter(Boolean)
      : [groupConfig.groupBy];
    if (groupByKeys.length === 0) return processedData;

    const subtotalCols = groupConfig.subtotalColumns || [];
    const showSubtotals = groupConfig.showSubtotals !== false;

    // Place subtotal label in the first visible column
    const firstColKey = (columnOrderRef.current || columns.map(c => c.key))[0];

    const buildSubtotalRow = (labelValue, rows, level, primaryGroupValue) => {
      const subtotalRow = { __isSubtotal: true, __subtotalLevel: level };
      // Store group metadata for postSortRows / onFilterChanged matching
      subtotalRow.__groupValue = labelValue;
      if (level === 2) subtotalRow.__primaryGroup = primaryGroupValue;
      // Always place label in the first visible column
      subtotalRow[firstColKey] = `${labelValue} Subtotal`;
      subtotalCols.forEach(col => {
        subtotalRow[col] = rows.reduce((sum, r) => {
          const v = coerceNumeric(r[col]);
          return sum + (v ?? 0);
        }, 0);
      });
      return subtotalRow;
    };

    // Group rows by first key
    const primaryKey = groupByKeys[0];
    const primaryGroups = new Map();
    processedData.forEach(row => {
      const groupVal = row[primaryKey] ?? '(blank)';
      if (!primaryGroups.has(groupVal)) primaryGroups.set(groupVal, []);
      primaryGroups.get(groupVal).push(row);
    });

    const result = [];
    const secondaryKey = groupByKeys.length > 1 ? groupByKeys[1] : null;

    primaryGroups.forEach((rows, primaryVal) => {
      if (secondaryKey) {
        // Sub-group within primary group
        const subGroups = new Map();
        rows.forEach(row => {
          const subVal = row[secondaryKey] ?? '(blank)';
          if (!subGroups.has(subVal)) subGroups.set(subVal, []);
          subGroups.get(subVal).push(row);
        });

        subGroups.forEach((subRows, subVal) => {
          subRows.forEach(row => result.push(row));
          if (showSubtotals && subtotalCols.length > 0) {
            result.push(buildSubtotalRow(subVal, subRows, 2, primaryVal));
          }
        });
      } else {
        rows.forEach(row => result.push(row));
      }

      // Level 1 subtotal after all sub-groups
      if (showSubtotals && subtotalCols.length > 0) {
        result.push(buildSubtotalRow(primaryVal, rows, 1));
      }
    });

    return result;
  }, [processedData, groupConfig, columns]);

  // Conditional formatting - row style
  const getRowStyle = useCallback((params) => {
    // Style subtotal rows — level 1 bolder, level 2 lighter
    if (params.data?.__isSubtotal) {
      const level = params.data.__subtotalLevel || 1;
      return level === 1
        ? { backgroundColor: '#E3F2FD', fontWeight: 'bold' }
        : { backgroundColor: '#F5F5F5', fontWeight: 'bold', fontStyle: 'italic' };
    }
    if (!conditionalFormatting?.length || !params.data) return undefined;

    for (const rule of conditionalFormatting) {
      if (rule.scope !== 'row') continue;
      if (evaluateCondition(rule.condition, params.data)) {
        return rule.style;
      }
    }
    return undefined;
  }, [conditionalFormatting]);

  // Handle column events — use onDragStopped so the grid finalizes position before we read
  const onDragStoppedHandler = useCallback((event) => {
    if (!event.api) return;
    const allCols = event.api.getColumns();
    if (allCols) {
      const newOrder = allCols.map(c => c.getColId());
      onColumnMoved?.(newOrder);
    }
  }, [onColumnMoved]);

  const onColumnResizedHandler = useCallback((event) => {
    if (!event.finished || !event.column) return;
    const colId = event.column.getColId();
    const width = event.column.getActualWidth();
    onColumnResized?.(colId, width);
  }, [onColumnResized]);

  const onCellClickedHandler = useCallback((event) => {
    if (event.column) {
      onColumnSelected?.(event.column.getColId());
    }
  }, [onColumnSelected]);

  // Totals row (pinnedBottomRowData)
  const pinnedBottomData = useMemo(() => {
    if (!showTotalsRow || !processedData?.length) return undefined;

    const calcKeys = Object.keys(calculatedColumns || {});
    const allNumericKeys = [...NUMERIC_KEYS, ...calcKeys];

    const totals = {};
    // Put TOTALS label in the first column from the columns prop (stable across drag reorder)
    if (columns.length > 0) totals[columns[0].key] = 'TOTALS';

    const visibleKeys = columns.map(c => c.key);
    allNumericKeys.forEach(key => {
      if (visibleKeys.includes(key)) {
        totals[key] = processedData.reduce((sum, row) => {
          const val = coerceNumeric(row[key]);
          return sum + (val ?? 0);
        }, 0);
      }
    });

    return [totals];
  }, [showTotalsRow, processedData, columns, calculatedColumns]);

  // Keep subtotal rows at the bottom of their group after column sorting
  const postSortRows = useCallback((params) => {
    if (!groupConfig?.groupBy) return;
    const groupByKeys = Array.isArray(groupConfig.groupBy)
      ? groupConfig.groupBy.filter(Boolean)
      : [groupConfig.groupBy];
    if (groupByKeys.length === 0) return;

    const primaryKey = groupByKeys[0];
    const secondaryKey = groupByKeys.length > 1 ? groupByKeys[1] : null;
    const nodes = params.nodes;

    // Separate data nodes from subtotal nodes
    const dataNodes = [];
    const level1Subtotals = new Map();
    const level2Subtotals = new Map();

    nodes.forEach(node => {
      if (!node.data?.__isSubtotal) {
        dataNodes.push(node);
      } else if (node.data.__subtotalLevel === 2 && secondaryKey) {
        const subVal = node.data.__groupValue;
        const primaryVal = node.data.__primaryGroup;
        level2Subtotals.set(`${primaryVal}||${subVal}`, node);
      } else {
        const groupVal = node.data.__groupValue;
        level1Subtotals.set(groupVal, node);
      }
    });

    if (level1Subtotals.size === 0 && level2Subtotals.size === 0) return;

    // Group data nodes by primary key
    const primaryGroups = new Map();
    const primaryOrder = [];
    dataNodes.forEach(node => {
      const val = node.data?.[primaryKey] ?? '(blank)';
      if (!primaryGroups.has(val)) {
        primaryGroups.set(val, []);
        primaryOrder.push(val);
      }
      primaryGroups.get(val).push(node);
    });

    nodes.length = 0;
    primaryOrder.forEach(primaryVal => {
      const groupNodes = primaryGroups.get(primaryVal);

      if (secondaryKey) {
        // Sub-group within primary group
        const subGroups = new Map();
        const subOrder = [];
        groupNodes.forEach(node => {
          const subVal = node.data?.[secondaryKey] ?? '(blank)';
          if (!subGroups.has(subVal)) {
            subGroups.set(subVal, []);
            subOrder.push(subVal);
          }
          subGroups.get(subVal).push(node);
        });

        subOrder.forEach(subVal => {
          subGroups.get(subVal).forEach(n => nodes.push(n));
          const sub = level2Subtotals.get(`${primaryVal}||${subVal}`);
          if (sub) nodes.push(sub);
        });
      } else {
        groupNodes.forEach(n => nodes.push(n));
      }

      const sub = level1Subtotals.get(primaryVal);
      if (sub) nodes.push(sub);
    });
  }, [groupConfig]);

  // Recalculate subtotals when filters change so they reflect only visible rows
  const onFilterChanged = useCallback((event) => {
    if (!groupConfig?.groupBy || !event.api) return;
    const groupByKeys = Array.isArray(groupConfig.groupBy)
      ? groupConfig.groupBy.filter(Boolean)
      : [groupConfig.groupBy];
    if (groupByKeys.length === 0) return;

    const subtotalCols = groupConfig.subtotalColumns || [];
    if (!subtotalCols.length) return;

    const primaryKey = groupByKeys[0];
    const secondaryKey = groupByKeys.length > 1 ? groupByKeys[1] : null;

    // Collect visible (non-subtotal) rows after filtering
    const visibleRows = [];
    event.api.forEachNodeAfterFilter(node => {
      if (!node.data?.__isSubtotal) visibleRows.push(node.data);
    });

    // Recalculate each subtotal row
    event.api.forEachNode(node => {
      if (!node.data?.__isSubtotal) return;
      const level = node.data.__subtotalLevel || 1;

      let matchingRows;
      if (level === 2 && secondaryKey) {
        const subVal = node.data.__groupValue;
        const priVal = node.data.__primaryGroup;
        matchingRows = visibleRows.filter(r =>
          (r[primaryKey] ?? '(blank)') === priVal &&
          (r[secondaryKey] ?? '(blank)') === subVal
        );
      } else {
        const groupVal = node.data.__groupValue;
        matchingRows = visibleRows.filter(r => (r[primaryKey] ?? '(blank)') === groupVal);
      }

      subtotalCols.forEach(col => {
        node.data[col] = matchingRows.reduce((sum, r) => {
          const v = coerceNumeric(r[col]);
          return sum + (v ?? 0);
        }, 0);
      });
    });

    // Refresh subtotal row cells
    const subtotalNodes = [];
    event.api.forEachNode(node => {
      if (node.data?.__isSubtotal) subtotalNodes.push(node);
    });
    if (subtotalNodes.length > 0) {
      event.api.refreshCells({ rowNodes: subtotalNodes, force: true });
    }
  }, [groupConfig]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    filter: SubtotalSafeFilter,
    minWidth: 80,
  }), []);

  return (
    <div className="ag-theme-alpine" style={{ height: '100%', width: '100%' }}>
      <AgGridReact
        ref={gridRef}
        rowData={displayData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        animateRows={true}
        getRowStyle={getRowStyle}
        postSortRows={postSortRows}
        onFilterChanged={onFilterChanged}
        onDragStopped={onDragStoppedHandler}
        onColumnResized={onColumnResizedHandler}
        maintainColumnOrder={false}
        onCellClicked={onCellClickedHandler}
        pinnedBottomRowData={pinnedBottomData}
        suppressAggFuncInHeader={true}
        domLayout="normal"
      />
    </div>
  );
}
